import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarRange } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { todayIso, toIsoDate } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { HR_TAB_PATH } from './employee-options';

export const rosterPath = (rosterId: string) => `${HR_TAB_PATH.roster}&roster=${encodeURIComponent(rosterId)}`;

export function NewRosterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const backTo = safeReturnTo(params.get('returnTo'), HR_TAB_PATH.roster);
  const [initial] = useState(() => ({ name: '', periodStart: todayIso(), periodEnd: toIsoDate(new Date(Date.now() + 6 * 86_400_000)) }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => post<{ id: string }>('/hr/rosters', { name: form.name, periodStart: form.periodStart, periodEnd: form.periodEnd }),
    onSuccess: (roster) => {
      toast.success('Roster created');
      void queryClient.invalidateQueries({ queryKey: ['rosters'] });
      navigate(rosterPath(roster.id), { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.name.trim().length >= 2;
  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="New roster"
      icon={CalendarRange}
      backTo={backTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('admin', 'roster')} icon={CalendarRange}>
        <Field label="Name" required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Medical ward — week 34" />
        </Field>
        <Field label="From" required>
          <DatePicker value={form.periodStart} onChange={(value) => setForm({ ...form, periodStart: value })} />
        </Field>
        <Field label="To" required>
          <DatePicker value={form.periodEnd} onChange={(value) => setForm({ ...form, periodEnd: value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Create" loading={create.isPending} incomplete={!ready} cancelTo={backTo} />
    </FormScreen>
  );
}
